import DialogContentText from "@material-ui/core/DialogContentText";
import ActionDialog from "@saleor/components/ActionDialog";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton/ConfirmButton";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import useDownloadReport from "@saleor/hooks/useDownloadReport";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import useUser from "@saleor/hooks/useUser";
import { findValueInEnum, getStringOrPlaceholder, maybe } from "@saleor/misc";
import { getCurrentTimestamp } from "@saleor/reports/utils/misc";
import { ListViews } from "@saleor/types";
import {
  PayableStatusEnum,
  PayoutStatusEnum,
  SettlementStatusEnum
} from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import _ from "lodash";
import moment from "moment";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import PaybleStatusDialog from "../../components/PaybleStatusDialog";
import PayoutsDetailsPage from "../../components/PayoutsDetailsPage";
import { PayoutSettlementFormData } from "../../components/PayoutSettlementDialog";
import PayoutSettlementDialog from "../../components/PayoutSettlementDialog";
import { UpdatePayoutStatusDialog } from "../../components/UpdatePayoutStatusDialog";
import {
  useSettlementMutation,
  useUpdatePayableStatusMutation,
  useUpdatePayoutStatusMutation
} from "../../mutation";
import {
  usePayoutDetail,
  usePayoutsStoreOrdersDownload,
  usePayoutsStoreOrdersQuery,
  usePayoutStoreOrderDetails
} from "../../queries";
import { PayoutStoreOrdersUpdate } from "../../types/PayoutStoreOrdersUpdate";
import { UpdatePayableStatus } from "../../types/UpdatePayableStatus";
import { UpdatePayoutStatus } from "../../types/UpdatePayoutStatus";
import {
  payoutDetailsUrl,
  PayoutDetailsUrlDialog,
  PayoutDetailsUrlQueryParams,
  payoutsListUrl
} from "../../urls";
import {
  filterReportData,
  managerDownloadData,
  payoutUserRole,
  selectReportData
} from "../../utils";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";

interface PayoutDetailsProp {
  id: string;
  params: PayoutDetailsUrlQueryParams;
}

const PayoutDetails: React.FC<PayoutDetailsProp> = ({ id, params }) => {
  const navigate = useNavigator();
  const shop = useShop();
  const notify = useNotifier();
  const paginate = usePaginator();
  const intl = useIntl();
  const { user } = useUser();
  const payoutUser = payoutUserRole(user?.userPermissions);

  const dateNow = getCurrentTimestamp();
  const fileName = `${payoutUser?.filename}_${dateNow}.xlsx`;

  const currencySymbol = shop?.defaultCurrency || "PHP";
  const { updateListSettings, settings } = useListSettings(
    ListViews.PAYOUT_DETAILS_LIST
  );
  const paginationState = createPaginationState(settings.rowNumber, params);

  const [loadData, setLoadData] = useState(true);
  const [downloadPagination, setDownloadPagination] = useState("");
  const [downloadStatus, setDownloadStatus] = useState<
    ConfirmButtonTransitionState
  >("default");
  const { fields, filename, sheetName, title } = managerDownloadData;

  const reportVariables = React.useMemo(
    () => ({
      ...paginationState,
      after: downloadPagination,
      filter: {
        payoutId: id,
        ...getFilterVariables(params)
      },
      first: 1000
    }),
    [params]
  );

  const {
    data: reportData,
    loading: reportLoading
  } = usePayoutsStoreOrdersDownload({
    displayLoader: true,
    skip: loadData,
    variables: reportVariables
  });

  const { data: PayoutDetail } = usePayoutDetail({
    displayLoader: true,
    variables: {
      id
    }
  });

  const { data, refetch } = usePayoutsStoreOrdersQuery({
    displayLoader: true,
    variables: {
      ...paginationState,
      filter: {
        payoutId: id,
        ...getFilterVariables(params)
      }
    }
  });

  const { data: FinanceStoreOrderDetails } = usePayoutStoreOrderDetails({
    displayLoader: true,
    skip: !params.id,
    variables: {
      id: params.id
    }
  });

  const handleUpdateSuccess = (data: PayoutStoreOrdersUpdate) => {
    if (data?.payoutStoreOrdersUpdate?.errors?.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Save Changes"
        })
      });
      refetch();
      closeModal();
    }
  };

  const [settlementUpdate, settlementUpdateResult] = useSettlementMutation({
    onCompleted: handleUpdateSuccess
  });

  // Update payout status
  const handleUpdatePayoutStatusSuccess = (data: UpdatePayoutStatus) => {
    if (data?.updatePayoutStatus?.errors?.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage(
          {
            defaultMessage: "{values}"
          },
          {
            values:
              params.updateTo === "COMPLETED"
                ? "This payout is successfully completed"
                : "This report is successfully submitted to next level for the review"
          }
        )
      });
      closeModal();
      navigate(payoutsListUrl());
    }
  };

  const [
    updatePayoutStatus,
    updatePayoutStatusResult
  ] = useUpdatePayoutStatusMutation({
    onCompleted: handleUpdatePayoutStatusSuccess
  });

  // Update payable status
  const handleUpdatePayableStatusSuccess = (data: UpdatePayableStatus) => {
    if (data?.updatePayableStatus?.errors?.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Save Changes"
        })
      });
      closeModal();
      refetch();
    }
  };

  const [
    updatePayableStatus,
    updatePayableStatusResult
  ] = useUpdatePayableStatusMutation({
    onCompleted: handleUpdatePayableStatusSuccess
  });

  const tabs = getFilterTabs();

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  const handleTabChange = (tab: number) => {
    navigate(
      payoutsListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    navigate(payoutsListUrl());
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };

  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    createUrl: params => payoutDetailsUrl(id, params),
    getFilterQueryParam,
    navigate,
    params
  });
  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    data?.payoutStoreOrders?.pageInfo,
    paginationState,
    params
  );

  const filterOpts = getFilterOpts(params);

  const [openModal, closeModal] = createDialogActionHandlers<
    PayoutDetailsUrlDialog,
    PayoutDetailsUrlQueryParams
  >(navigate, params => payoutDetailsUrl(id, params), params);

  const decimalNumber = (value: number) => {
    if (value === null) {
      return null;
    }
    const strVal = value.toString();

    return Number(parseFloat(strVal).toFixed(2));
  };

  const handleUpdateStatus = async () => {
    const result = await updatePayoutStatus({
      variables: {
        id,
        status: findValueInEnum(params.updateTo, PayoutStatusEnum)
      }
    });

    return result?.data?.updatePayoutStatus?.errors;
  };

  const handleSubmit = async (data: PayoutSettlementFormData) => {
    const result = await settlementUpdate({
      variables: {
        id: params?.id,
        input: {
          other_adjustment: decimalNumber(data.otherAdjustment) || 0,
          payment_fee_adjustment: decimalNumber(data.paymentFeeAdjustment) || 0,
          payment_fee_adjustment_vat:
            decimalNumber(data.paymentFeeAdjustmentVat) || 0,
          settlement_date: moment(`${data.settlementDate} 00:00:00`).format(
            "YYYY-MM-DD HH:mm:ss.SSSZZ"
          ),
          settlement_status: maybe(
            () => findValueInEnum(data.settlementStatus, SettlementStatusEnum),
            SettlementStatusEnum.FOR_PAYMENT
          )
        }
      }
    });

    return result?.data?.payoutStoreOrdersUpdate?.errors;
  };

  const [downloadData, setDownloadData] = useState([]);

  const allPayoutStoreOrder = reportData?.payoutStoreOrders?.edges.map(
    edge => edge.node
  );
  const nextPage = reportData?.payoutStoreOrders?.pageInfo;

  React.useEffect(() => {
    if (reportData && !reportLoading) {
      setDownloadData(current => [...current, allPayoutStoreOrder]);
      setDownloadPagination(nextPage?.endCursor);
      if (!nextPage?.hasNextPage && nextPage?.endCursor === null) {
        const selectedReportData = selectReportData(
          downloadData?.flat(),
          payoutUser
        );
        useDownloadReport(
          selectedReportData,
          payoutUser.fields,
          payoutUser.sheetName,
          fileName
        );
        setDownloadStatus("success");
        setDownloadPagination("");
        setLoadData(true);
        setDownloadData([]);
        notify({
          status: "success",
          text: intl.formatMessage(
            {
              defaultMessage: "{value} successfully downloaded."
            },
            {
              value: payoutUser.title
            }
          )
        });
        window.open("", "Download").close();
        closeModal();
      }
    }
  }, [reportData]);

  const cancelDownload = () => {
    setLoadData(true);
    setDownloadData([]);
    setDownloadStatus("default");
    setDownloadPagination("");
    closeModal();
  };

  const payoutStatus = maybe(() => PayoutDetail?.payout?.payout_status);

  const hasData = data?.payoutStoreOrders?.edges.map(edge => edge.node);

  function handleManagerDownload() {
    const value = filterReportData(hasData, fields);

    useDownloadReport(value, fields, sheetName, `${filename}_${dateNow}.xlsx`);

    notify({
      status: "success",
      text: intl.formatMessage(
        {
          defaultMessage: "{value} successfully downloaded."
        },
        {
          value: title
        }
      )
    });
    window.open("", "Download").close();
    closeModal();
  }
  return (
    <>
      <PayoutsDetailsPage
        currencySymbol={currencySymbol}
        currentTab={currentTab}
        disabled={false}
        filterOpts={filterOpts}
        id={id}
        initialSearch={params.query || ""}
        onAll={resetFilters}
        onBack={() => navigate(payoutsListUrl())}
        onFilterChange={changeFilters}
        onRowClick={() => undefined}
        onSearchChange={handleSearchChange}
        onTabChange={handleTabChange}
        download={() => openModal("download")}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        user={user}
        onSettlementEdit={id =>
          openModal("settlement", {
            id
          })
        }
        onReview={updateTo =>
          openModal("review", {
            updateTo
          })
        }
        onPayable={id =>
          openModal("payable", {
            id
          })
        }
        onDownloadName={maybe(() => payoutUser.button)}
        pageInfo={pageInfo}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        settings={settings}
        payouts={maybe(() =>
          data.payoutStoreOrders.edges.map(edge => edge.node)
        )}
        payoutStatus={payoutStatus}
        tabs={tabs.map(tab => tab.name)}
        onManagerDownload={() => openModal("download-all")}
      />

      <PayoutSettlementDialog
        open={params.action === "settlement"}
        onClose={closeModal}
        onSubmit={handleSubmit}
        errors={
          settlementUpdateResult?.data?.payoutStoreOrdersUpdate?.errors || []
        }
        confirmButtonState={settlementUpdateResult?.status}
        FinanceStoreOrderDetails={FinanceStoreOrderDetails?.payoutStoreOrder}
      />

      <PaybleStatusDialog
        open={params.action === "payable"}
        onClose={closeModal}
        onSubmit={formData =>
          updatePayableStatus({
            variables: {
              id: params?.id,
              payableStatus: maybe(
                () =>
                  findValueInEnum(formData.payableStatus, PayableStatusEnum),
                PayableStatusEnum.PAID
              )
            }
          })
        }
        errors={
          updatePayableStatusResult?.data?.updatePayableStatus?.errors || []
        }
        confirmButtonState={updatePayableStatusResult?.status}
        FinanceStoreOrderDetails={FinanceStoreOrderDetails?.payoutStoreOrder}
      />

      <ActionDialog
        open={params.action === "download"}
        onClose={cancelDownload}
        confirmButtonState={downloadStatus}
        onConfirm={() => {
          setLoadData(false);
          setDownloadStatus("loading");
          setDownloadData([]);
          setDownloadPagination("");
        }}
        variant="default"
        title={intl.formatMessage(
          {
            defaultMessage: "Download {value}",
            description: "dialog title"
          },
          {
            value: payoutUser.title
          }
        )}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="You are about to download a {title} with {total} rows. Do you want to continue?"
            values={{
              title: payoutUser.title,
              total: <strong>{data?.payoutStoreOrders?.totalCount}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>

      <ActionDialog
        open={params.action === "download-all"}
        onClose={cancelDownload}
        confirmButtonState={downloadStatus}
        onConfirm={handleManagerDownload}
        variant="default"
        title={intl.formatMessage(
          {
            defaultMessage: "Download {value}",
            description: "dialog title"
          },
          {
            value: title
          }
        )}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="You are about to download a {title} with {total} rows. Do you want to continue?"
            values={{
              title,
              total: <strong>{data?.payoutStoreOrders?.totalCount}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>

      <UpdatePayoutStatusDialog
        open={params.action === "review"}
        onClose={closeModal}
        updateTo={params.updateTo}
        confirmButtonState={updatePayoutStatusResult.status}
        onConfirm={handleUpdateStatus}
      />

      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />
      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={getStringOrPlaceholder(tabs[currentTab - 1]?.name)}
      />
    </>
  );
};
export default PayoutDetails;
